import { injectMetaData } from './MetaDataInjector';
import logService from '../../../../services/wss/logService3';

const _name = 'IframeRendererHelper';

/**
 * Parses meta fields from the first line of a template.
 * Format: <!-- meta: key=value key2=value2 -->
 * Returns empty object if no meta comment found.
 * @param {string} html - template HTML body
 * @returns {object} key/value map of meta fields
 */
export const parseMeta = (html) => {
    const match = html.match(/^<!--\s*meta:\s*(.+?)\s*-->/);
    if (!match) return {};
    return Object.fromEntries(match[1].split(/\s+/).map((pair) => pair.split('=')));
};

/**
 * Builds the final HTML by injecting theme CSS, widget JS/HTML, data JSON, settings JSON and resolving placeholders.
 * @param {string} templateBody - HTML string from template note
 * @param {string} dataBody - JSON string from data note (entry.body)
 * @param {Function} getTemplate - fn(rk) => template note or null
 * @param {object} entry - data note entry object — used for metadata injection
 * @param {object|null} template - template note object — used for templateName in metadata
 * @param {string} dateFormat - user date format string
 * @param {object} settings - user settings object injected as const settings = {...}
 * @param {Function} getTemplateByFormat - fn(format) => template note or null, used for widget resolution
 * @returns {string|null} final HTML string or null on failure
 */
export const buildHtml = (templateBody, dataBody, getTemplate, entry, template, dateFormat, settings = {}, getTemplateByFormat = null) => {
    try {
        // Parse data note body — bail out if not valid JSON
        let parsed;
        try {
            parsed = JSON.parse(dataBody);
        } catch (e) {
            logService.logError(`${_name}.buildHtml: dataBody is not valid JSON — ${e.message}`);
            return null;
        }

        // Enrich parsed data with entry metadata — _meta block added, no existing fields overwritten
        const enriched = injectMetaData(parsed, entry, template?.title ?? null, dateFormat);

        // Check of templateBody een index is (JSON met metaRk + htmlRk)
        // Index patroon: {"metaRk":"...","htmlRk":"..."}
        let metaBody = templateBody;
        let htmlBody = templateBody;
        try {
            const index = JSON.parse(templateBody);
            if (index.metaRk && index.htmlRk) {
                const metaNote = getTemplate(index.metaRk);
                const htmlNote = getTemplate(index.htmlRk);
                if (!metaNote || !htmlNote) {
                    logService.logError(`${_name}.buildHtml: index metaRk or htmlRk not found in cache`);
                    return null;
                }
                metaBody = metaNote.body ?? '';
                htmlBody  = htmlNote.body  ?? '';
                // logService.log(`${_name}.buildHtml: index resolved — metaRk=${index.metaRk} htmlRk=${index.htmlRk}`);
            }
        } catch (_) {
            // Geen index — templateBody is gewone HTML, doorgaan
        }

        // Extract meta fields from meta body — e.g. themeId, template_widgets, layout_config
        const meta = parseMeta(metaBody);
        // logService.log(`${_name}.buildHtml: meta parsed — ${JSON.stringify(meta)}`);

        // Fetch theme CSS — themeId kan een index zijn met komma-lijst van sub-theme formats
        // Als body komma-lijst is: elk format ophalen via getTemplateByFormat + concat
        // Als geen sub-themes gevonden: body is gewone CSS — direct gebruiken
        const themeBody = (() => {
            if (!meta.themeId) return '';
            const themeNote = getTemplate(meta.themeId);
            if (!themeNote) return '';
            const body = themeNote.body ?? '';
            const parts = body.split(/[,\n]/).map((f) => f.trim()).filter(Boolean);
            const resolved = parts
                .map((f) => getTemplateByFormat ? (getTemplateByFormat(f)?.body ?? '') : '')
                .filter(Boolean);
            // Minstens één sub-theme gevonden — concat en gebruik
            if (resolved.length > 0) {
                // logService.log(`${_name}.buildHtml: theme resolved via index — ${parts.length} sub-themes`);
                return resolved.join('\n');
            }
            // Geen sub-themes — body is gewone CSS
            return body;
        })();

        // Inject theme CSS into <head> after existing styles — theme overrides template defaults
        const withTheme = themeBody ? htmlBody.replace('</head>', `<style>${themeBody}</style>\n</head>`) : htmlBody;

        // Inject widgets — resolve by format=widget_{name} from template_widgets meta
        const widgetNames = (meta.template_widgets ?? '')
            .split(',')
            .map((w) => w.trim())
            .filter(Boolean);

        // logService.log(`${_name}.buildHtml: widgetNames — ${JSON.stringify(widgetNames)}`);
        const widgetHtml = widgetNames
            .map((name) => {
                if (!getTemplateByFormat) {
                    logService.logError(`${_name}.buildHtml: getTemplateByFormat is null — widget ${name} skipped`);
                    return '';
                }
                const widget = getTemplateByFormat(`${name}_widget_template`);
                if (!widget) {
                    logService.logError(`${_name}.buildHtml: widget not found in cache — format=widget_${name}`);
                    return '';
                }
                // logService.log(`${_name}.buildHtml: widget injected — format=widget_${name} rk=${widget.rk}`);
                return widget.body ?? '';
            })
            .join('\n');
        const withWidgets = widgetHtml ? withTheme.replace('</body>', `${widgetHtml}\n</body>`) : withTheme;

        // Replace const data = {} placeholder with real data JSON from entry body
        const injected = withWidgets.replace(/const data = \{[\s\S]*?\};/, `const data = ${JSON.stringify(enriched, null, 2)};`);

        // Replace const settings = {} placeholder with user settings JSON
        const withSettings = injected.replace(/const settings = \{[\s\S]*?\};/, `const settings = ${JSON.stringify(settings, null, 2)};`);

        // Inject layout config from template meta — sync, no postMessage needed
        const layoutConfigJson = meta.layout_config ? meta.layout_config : '{}';
        const withLayoutConfig = withSettings.replace(/const layoutConfig = \{[\s\S]*?\};/, `const layoutConfig = ${layoutConfigJson};`);

        // Replace {{placeholders}} with scalar values — arrays joined with · separator
        const replaced = withLayoutConfig.replace(/\{\{(\w+)\}\}/g, (match, key) => {
            const val = enriched[key];
            if (val === undefined || val === null) return '';
            if (Array.isArray(val)) return val.join(' · ');
            return String(val);
        });

        // ADDED — rewrite all <a href="https?://..."> to use postMessage
        // Sandbox blocks window.open/popups — parent catches jerny:openurl and opens in new tab
        const withLinks = replaced.replace(/<a\s([^>]*href="(https?:\/\/[^"]+)"[^>]*)>/gi, (match, attrs, url) => {
            const safeUrl = url.replace(/'/g, "\\'");
            return `<a ${attrs} onclick="window.parent.postMessage({type:'jerny:openurl',url:'${safeUrl}'},'*');return false;">`;
        });

        // Validate resulting HTML — bail out if browser cannot parse it
        const doc = new DOMParser().parseFromString(withLinks, 'text/html');
        if (doc.querySelector('parsererror')) return null;

        return withLinks;
    } catch (error) {
        logService.logError(`${_name}.buildHtml: unexpected error — ${error.message}`);
        return null;
    }
};
